import { useMemo } from "react";

import { AnalysisReportEvents } from "shared/types";
import { createMDWrapper } from "shared/lib";
import { SubdivisionType } from "shared/hooks";

import * as approvedDevelopment from "./alreadyApprovedDevelopment";
import * as battleAxe from "./smallSubdivision";
import * as largeLotInRuralArea from "./largeSubdivision";
import * as revisingExistingBoundaries from "./revisingExistingBoundaries";

export const ItemType = {
  planningApproval: "planningApproval",
  certification: "certification",
  works: "works",
  statementOfCompliance: "statementOfCompliance",
  titleRegistration: "titleRegistration",
} as const;
export type ItemType = (typeof ItemType)[keyof typeof ItemType];

export interface SubdivisionTimelineItemContentProps {
  subdivisionType: SubdivisionType;
  itemName: ItemType;
}

export function SubdivisionTimelineItemContent({
  subdivisionType,
  itemName,
}: SubdivisionTimelineItemContentProps) {
  const content = useMemo(() => {
    switch (subdivisionType) {
      case SubdivisionType["subdivision of an approved development"]:
        return approvedDevelopment[itemName].default;
      case SubdivisionType["battle-axe subdivision"]:
        return battleAxe[itemName].default;
      case SubdivisionType["large lot subdivision in a rural area"]:
        return largeLotInRuralArea[itemName].default;
      case SubdivisionType["revising existing boundaries"]:
        return revisingExistingBoundaries[itemName].default;
      default:
        return "";
    }
  }, [subdivisionType, itemName]);

  return (
    <div>
      {createMDWrapper(content, {
        components: {
          a: ({ children, href }) => {
            if (href === "#application_requirements") {
              return (
                <a
                  className="text-textLink hover:text-textLinkHover text-bodymd underline"
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();

                    const customEvent = new CustomEvent(
                      AnalysisReportEvents.SHOW_APPLICATION_REQUIREMENTS,
                    );
                    document.dispatchEvent(customEvent);
                  }}
                >
                  {children}
                </a>
              );
            } else {
              return (
                <a
                  href={href}
                  className="text-textLink hover:text-textLinkHover text-bodymd underline"
                  rel="noreferrer"
                  target="_blank"
                  tabIndex={0}
                >
                  {children}
                </a>
              );
            }
          },
          p: ({ children }) => (
            <p className="text-bodymd text-textPrimary my-1">{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="text-bodymd mb-3 ml-6 list-outside list-disc">
              {children}
            </ul>
          ),
          li: ({ children }) => (
            <li className="mt-1">
              <span className="text-bodymd relative">{children}</span>
            </li>
          ),
          h2: ({ children }) => (
            <h2 className="text-textPrimary text-headingsm my-1 font-semibold">
              {children}
            </h2>
          ),
          table: ({ children }) => (
            <div className="border-interfaceBorder w-full overflow-x-auto rounded border">
              <table className="w-full">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="text-textPrimary text-headingsm bg-interfaceBgDefault px-2 py-1 text-left font-semibold">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="text-textPrimary text-bodymd bg-textWhite px-2 py-1">
              {children}
            </td>
          ),
        },
      })}
    </div>
  );
}
